/**
 * Google OAuth Utilities
 *
 * Provides functions for Google OAuth operations including
 * initial connection and incremental authorization for additional scopes.
 */

import { SignJWT } from "jose";
import type { SupabaseClient } from "@/lib/supabase";
import {
  type GoogleScopeType,
  type OAuthCredentials,
  type IncrementalOAuthParams,
  type OAuthUrlResult,
  GOOGLE_SCOPE_CONFIGS,
} from "./types";

export const GOOGLE_AUTH_URL = "https://accounts.google.com/o/oauth2/v2/auth";

type KVSetting = { key: string; value: string | null };

/**
 * Get Google OAuth credentials from system settings (key/value format).
 */
export async function getGoogleCredentials(
  supabase: SupabaseClient
): Promise<OAuthCredentials> {
  const { data, error } = await supabase
    .from("system_settings")
    .select("key,value")
    .in("key", ["google_client_id", "google_client_secret"]);

  if (error) {
    throw new Error(`Failed to fetch Google credentials: ${error.message}`);
  }

  if (!Array.isArray(data) || data.length === 0) {
    throw new Error("Google OAuth credentials not found in system_settings");
  }

  const map = new Map(
    (data as KVSetting[]).map((row) => [row?.key, row?.value ?? ""])
  );
  const clientId = map.get("google_client_id") || "";
  const clientSecret = map.get("google_client_secret") || "";

  if (!clientId || !clientSecret) {
    throw new Error("Google OAuth credentials are incomplete in system_settings");
  }

  return { clientId, clientSecret };
}

/**
 * Sign OAuth state payload as JWT
 */
export async function signOAuthState(
  payload: Record<string, unknown>,
  secret: string
): Promise<string> {
  const key = new TextEncoder().encode(secret);
  return new SignJWT(payload)
    .setProtectedHeader({ alg: "HS256" })
    .setIssuedAt()
    .setExpirationTime("10m")
    .sign(key);
}

/**
 * Parameters for building initial Google OAuth URL
 */
export interface InitialOAuthParams {
  userUuid: string;
  /**
   * Tenant the integration is being connected FOR (impersonation-aware active
   * tenant from the connect route). The callback persists oauth_tokens /
   * email_inboxes under this tenant. Without it the callback would fall back to
   * the acting user's home tenant, which is wrong when a platform-staff user
   * (super_user / integrator) connects Google while impersonating a client tenant.
   */
  tenantId: string;
  attachToAgent?: string;
  returnTo?: string;
  origin: string;
}

/**
 * Build Google OAuth URL for initial Gmail connection
 */
export async function buildGoogleInitialOAuthUrl(
  params: InitialOAuthParams,
  credentials: OAuthCredentials,
  jwtSecret: string
): Promise<OAuthUrlResult> {
  const scopeConfig = GOOGLE_SCOPE_CONFIGS.gmail_base;

  // Build state payload. The signed state carries the persistence tenant so the
  // callback writes the integration under the tenant being connected FOR, not
  // the acting user's home tenant (matters under impersonation).
  const statePayload: Record<string, unknown> = {
    user_uuid: params.userUuid,
    tenant_id: params.tenantId,
    return_to: params.returnTo || "/?page=integrations",
  };
  if (params.attachToAgent) {
    statePayload.attach_to_agent = params.attachToAgent;
  }

  const state = await signOAuthState(statePayload, jwtSecret);

  // Build OAuth URL
  const url = new URL(GOOGLE_AUTH_URL);
  url.searchParams.set("client_id", credentials.clientId);
  url.searchParams.set("redirect_uri", `${params.origin}/api/auth/google/callback`);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("scope", scopeConfig.scopes.join(" "));
  url.searchParams.set("access_type", "offline");
  url.searchParams.set("include_granted_scopes", "true");
  url.searchParams.set("prompt", "consent");
  url.searchParams.set("state", state);

  return {
    authUrl: url.toString(),
    state,
  };
}

/**
 * Build Google OAuth URL for incremental authorization
 *
 * Uses the same callback as main Gmail OAuth to avoid registering
 * multiple redirect URIs in Google Console.
 */
export async function buildGoogleIncrementalOAuthUrl(
  params: IncrementalOAuthParams,
  credentials: OAuthCredentials,
  jwtSecret: string
): Promise<OAuthUrlResult> {
  const scopeConfig = GOOGLE_SCOPE_CONFIGS[params.scopeType];

  if (!scopeConfig) {
    throw new Error(`Unknown scope type: ${params.scopeType}`);
  }

  if (!scopeConfig.stateType) {
    throw new Error(`Scope type ${params.scopeType} cannot be used for incremental authorization`);
  }

  // Build state payload
  const statePayload = {
    type: scopeConfig.stateType,
    inbox_id: params.inboxId,
    oauth_token_id: params.oauthTokenId,
    return_to: params.returnTo || "/?page=integrations",
  };

  const state = await signOAuthState(statePayload, jwtSecret);

  // Build OAuth URL
  const url = new URL(GOOGLE_AUTH_URL);
  url.searchParams.set("client_id", credentials.clientId);
  url.searchParams.set("redirect_uri", `${params.origin}/api/auth/google/callback`);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("scope", scopeConfig.scopes.join(" "));
  url.searchParams.set("access_type", "offline");
  // Include granted scopes to preserve existing permissions
  url.searchParams.set("include_granted_scopes", "true");
  url.searchParams.set("prompt", "consent");
  url.searchParams.set("state", state);

  // Pre-fill email to ensure correct account selection
  if (params.loginHintEmail) {
    url.searchParams.set("login_hint", params.loginHintEmail);
  }

  return {
    authUrl: url.toString(),
    state,
  };
}

/**
 * Full Google Calendar scope — a superset of the incremental-grant pair.
 * Tokens connected outside our incremental flow may carry it instead.
 */
const GOOGLE_FULL_CALENDAR_SCOPE = "https://www.googleapis.com/auth/calendar";

/**
 * Google Calendar BOOKING access: the token can both read free/busy AND
 * create/edit events. True when the app's incremental-grant pair is
 * present (`GOOGLE_SCOPE_CONFIGS.calendar`: calendar.events +
 * calendar.events.freebusy) or when the token carries the broader full
 * calendar scope.
 *
 * Use THIS — not a substring match like `s.includes('calendar')` — for
 * any "can the agent book meetings?" decision: a substring match accepts
 * read-only / freebusy-only tokens that pass availability checks but
 * fail at event creation.
 */
export function hasGoogleCalendarAccess(
  scopes: string[] | null | undefined
): boolean {
  if (!scopes || !Array.isArray(scopes)) {
    return false;
  }
  if (scopes.includes(GOOGLE_FULL_CALENDAR_SCOPE)) {
    return true;
  }
  return hasGoogleScope(scopes, "calendar");
}

/**
 * Check if a scope is present in the token's scopes array
 */
export function hasGoogleScope(
  scopes: string[] | null | undefined,
  scopeType: GoogleScopeType
): boolean {
  if (!scopes || !Array.isArray(scopes)) {
    return false;
  }

  const scopeConfig = GOOGLE_SCOPE_CONFIGS[scopeType];
  if (!scopeConfig) {
    return false;
  }

  // Check if ALL required scopes are present
  return scopeConfig.scopes.every((requiredScope) =>
    scopes.some(
      (s) =>
        s === requiredScope ||
        // Handle short form (e.g., "calendar.events" matches full URL)
        requiredScope.endsWith(`/${s}`)
    )
  );
}
